<template>
    <div class="generator-visual-inspection">
        <div v-if="sortedReports.length !== 0">
            <div class="generator-visual-inspection__header">
                <span class="generator-visual-inspection__header__title">
                    {{ t('generators.conditionStatus.content.visualInspections.status.label') }}
                </span>
                <ColorBadge
                    :color="reportStatus.color"
                    :text="t(reportStatus.labelKey)"
                />
                <div v-if="hasOverride" class="generator-visual-inspection__header__override">
                    <RefreshCcwDotIcon
                        v-tooltip="{
                            content: overrideTooltipHTML,
                            distance: 10,
                            placement: 'bottom-start',
                            html: true,
                        }"
                    />
                </div>
            </div>
            <div class="generator-visual-inspection__table-wrap">
                <div
                    ref="tableRef"
                    class="generator-visual-inspection__table"
                ></div>
            </div>
        </div>
        <EmptyStatePanelContent v-else />
    </div>
</template>

<script setup lang="ts">
import { useGeneratorVisualInspectionsQuery } from '@/api/queries/generators/useGeneratorVisualInspectionsQuery'
import { useConstantsQuery } from '@/api/queries/general/useConstantsQuery'
import { useGeneratorVisualInspectionTableRoutes } from '@/composables/useGeneratorVisualInspectionTableRoutes'
import { useGeneratorId } from '@/composables/useGeneratorId'
import ColorBadge from '@/components/badge/ColorBadge.vue'
import EmptyStatePanelContent from '@/components/states/EmptyStatePanelContent.vue'
import { ConditionStatus, StatusTypes } from '@/configuration/status/condition-status'
import { DATE_FORMAT } from '@/utils/date-utils'
import { generatorViReportColumnField, getGeneratorViReportHeaderLabel } from '@/pages/generators/condition-status/sections/visual-inspections/generatorVisualInspectionReportUtils'
import {
    GENERATOR_VISUAL_INSPECTION_TABULATOR_DEFAULTS,
    buildGeneratorVisualInspectionReportTitleHeaderElement,
    createGeneratorVisualInspectionCommentFormatter,
    createGeneratorVisualInspectionCommentTooltip,
    createGeneratorVisualInspectionImagesForCommentFormatter,
    createGeneratorVisualInspectionStatusNumberFormatter,
} from '@/pages/generators/condition-status/sections/visual-inspections/generatorVisualInspectionTableFormatters'
import type { VisualInspectionParameterValueDto } from '@/api/generated/HCA'
import { PebbleTable } from '@pebble/tables'
import { format } from 'date-fns'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { ColumnDefinition, Options } from 'tabulator-tables'
import { createElement, RefreshCcwDot } from 'lucide'
import { RefreshCcwDotIcon } from 'lucide-vue-next'
import { useOverrideReportStatusModal } from '@/components/modals/override-report-status/useOverrideReportStatusModal'
import { ReportType } from '@/configuration/report/report-types'
import { ComponentType } from '@/configuration/component/component-types'
import { useComponentStatusQuery } from '@/api/queries/general/useComponentStatusQuery'
import { AnalysisType } from '@/configuration/analysis/analysis-types'

type RowData = Record<string, string | number | VisualInspectionParameterValueDto | undefined>

const generatorId = useGeneratorId()
const { t } = useI18n()
const { openReport, onViewImagesClick } = useGeneratorVisualInspectionTableRoutes(generatorId)

const overrideModal = useOverrideReportStatusModal()

const { data: constantsData } = useConstantsQuery()
const { data } = useGeneratorVisualInspectionsQuery(generatorId)
const { data: statusData } = useComponentStatusQuery(generatorId, ComponentType.Generator)

const sortedReports = computed(() => {
    const items = data.value?.items ?? []
    return [...items].sort((a, b) => {
        const ta = new Date(a.date ?? 0).getTime()
        const tb = new Date(b.date ?? 0).getTime()
        return tb - ta
    })
})

const reportStatus = computed(() => {
    const status = statusData.value?.latestConditionStatuses?.find((e) => e.reportType === AnalysisType.VisualInspection)?.status
    return StatusTypes[status ?? ConditionStatus.None]
})

const hasOverride = computed(() => {
    return !!data.value?.items?.at(0)?.overrideComment
})

const overrideTooltipHTML = computed(() => {
    const overrideComment = data.value?.items?.at(0)?.overrideComment

    if (!overrideComment) {
        return ''
    }

    const title = t('conditionStatus.content.oilAnalyses.overrideComment.title', {
        from: t(StatusTypes.find((e) => e.value === overrideComment.previousStatus)?.labelKey ?? ''),
        to: t(StatusTypes.find((e) => e.value === overrideComment.newStatus)?.labelKey ?? ''),
    })

    const date = format(new Date(overrideComment.date ?? new Date()), DATE_FORMAT)
    const kid = overrideComment.kid ?? 'N/A'

    let commentText = overrideComment.text ?? ''
    
    if (commentText.length > 300) {
        commentText = commentText.substring(0, 300) + '...'
    }

    return `
        ${title}
        <br><br>
        ${commentText}
        <br/><br/>
        ${kid} | ${date}
    `
})

const statusNumberFormatter = createGeneratorVisualInspectionStatusNumberFormatter(t)
const commentFormatter = createGeneratorVisualInspectionCommentFormatter()
const commentTooltip = createGeneratorVisualInspectionCommentTooltip()
const imagesForCommentFormatter = createGeneratorVisualInspectionImagesForCommentFormatter()

// @ts-expect-error ColumnDefinition doesn't properly type Tooltip return type.
const columns = computed<ColumnDefinition[]>(() => {
    if (!sortedReports.value.length) {
        return []
    }

    return [
        {
            title: t('generators.conditionStatus.content.reportNumber'),
            columns: [
                {
                    title: t('generators.conditionStatus.content.measurement'),
                    field: 'parameterName',
                    headerSort: false,
                    minWidth: 200,
                },
            ],
            frozen: true,
        },
        ...sortedReports.value.map((report, index) => {
            const colId = generatorViReportColumnField(report, index)
            const analyticalNumber = report.analyticalNumber?.trim() ?? ''
            const headerLabel = getGeneratorViReportHeaderLabel(report, index, generatorId.value?.trim() ?? '', t)
            return {
                title: headerLabel,
                titleFormatter: () => {
                    const date = format(new Date(report.date ?? new Date()), DATE_FORMAT)
                    return buildGeneratorVisualInspectionReportTitleHeaderElement({
                        t,
                        headerLabel,
                        analyticalNumber,
                        dateLabel: date,
                        onOpenReport: openReport,
                        hasImages: !!report.hasImages,
                        onViewImages: onViewImagesClick,
                        hasOverride: !!report.overrideComment,
                    })
                },
                headerContextMenu: () => {
                    return [
                        {
                            label: (): HTMLElement => {
                                const cellDiv = document.createElement('div')
                                cellDiv.classList.add('analysis-table-context-menu-item')

                                const iconDiv = createElement(RefreshCcwDot)
                                cellDiv.appendChild(iconDiv)

                                const spanDiv = document.createElement('span')
                                spanDiv.textContent = t('transformers.conditionStatus.content.overrideModal.title')
                                cellDiv.appendChild(spanDiv)

                                return cellDiv
                            },
                            action: () => {
                                overrideModal?.openOverride({
                                    reportType: ReportType.VisualInspection,
                                    reportId: report.id ?? -1,
                                    reportName: report.analyticalNumber ?? '',
                                    status: reportStatus.value.value,
                                })
                            },
                        },
                    ]
                },
                columns: [
                    {
                        title: t('transformers.conditionStatus.content.visualInspections.table.status'),
                        field: colId,
                        headerSort: false,
                        minWidth: 100,
                        formatter: statusNumberFormatter,
                    },
                    {
                        title: t('transformers.conditionStatus.content.visualInspections.table.comment'),
                        field: colId,
                        headerSort: false,
                        minWidth: 160,
                        maxWidth: 280,
                        formatter: commentFormatter,
                        tooltip: commentTooltip,
                    },
                    {
                        title: t('generators.conditionStatus.content.visualInspections.table.photosForComment'),
                        field: colId,
                        headerSort: false,
                        minWidth: 160,
                        maxWidth: 280,
                        formatter: imagesForCommentFormatter,
                    },
                ],
                frozen: index === 0,
            }
        }),
    ]
})

const tableData = computed(() => {
    if (!constantsData.value || !sortedReports.value.length) {
        return []
    }

    const availableParameters = new Set<number>()
    sortedReports.value.forEach((report) => {
        report.inspectionParameterValues?.forEach((parameter) => {
            if (parameter.parameterId) {
                availableParameters.add(parameter.parameterId)
            }
        })
    })

    const rowData: RowData[] = []

    availableParameters.forEach((availableParameter) => {
        const constantsRecord = constantsData.value?.visualInspectionParameters?.find((e) => e.id === availableParameter)
        if (!constantsRecord) {
            return
        }
        rowData.push({
            parameterId: availableParameter,
            parameterName: t(
                `transformers.conditionStatus.content.visualInspections.table.measurementPoints.${constantsRecord.name}`,
            ),
        })
    })

    sortedReports.value.forEach((report, reportIndex) => {
        const columnId = generatorViReportColumnField(report, reportIndex)
        report.inspectionParameterValues?.forEach((parameter) => {
            const rowRecord = rowData.find((e) => e.parameterId === parameter.parameterId)
            if (rowRecord) {
                rowRecord[columnId] = parameter
            }
        })
    })

    return rowData
})

const tableRef = ref<HTMLDivElement | null>(null)
const pebbleTable = ref<PebbleTable | null>(null)

watch([tableRef, tableData], ([newTableRef, newTableData]) => {
    if (!newTableRef) {
        return
    }

    const tabulatorOptions: Options = {
        ...GENERATOR_VISUAL_INSPECTION_TABULATOR_DEFAULTS,
        columns: columns.value,
        data: newTableData,
    }

    pebbleTable.value?.destroy()
    pebbleTable.value = new PebbleTable(newTableRef, tabulatorOptions)
    pebbleTable.value.on('tableBuilt', () => { pebbleTable.value?.redraw(true) })
})
</script>

<style scoped lang="scss">
.generator-visual-inspection {
    &__header {
        display: flex;
        align-items: center;
        gap: 1em;

        &__title {
            font-size: $default-font-size;
            font-weight: bold;
        }

        &__override {
            display: flex;
            
            svg {
                height: 18px;
                width: 18px;
            }
        }
    }

    &__table-wrap {
        margin-top: $spacing-s;
        max-width: 100%;
    }

    &__table {
        border: 1px solid $pbl-border-muted;
        width: 100%;
    }
}
</style>

<style lang="scss">
.generator-visual-inspection__header-cell {
    display: flex;
    gap: 4px;

    &__link {
        color: $pbl-primary;
        font-weight: 600;
        cursor: pointer;

        &:hover {
            opacity: 0.8;
        }
    }

    &__date {
        color: $pbl-text-muted;
        font-weight: normal;
    }

    &__icon {
        width: 1rem;
        height: 1rem;
    }
}

.generator-visual-inspection__status-cell {
    display: flex;
    align-items: center;
    gap: $spacing-2xs;
}

.generator-visual-inspection__status-icon {
    --size: 1.1rem;

    &.text-warning {
        color: $pbl-warning;
    }

    &.text-danger {
        color: $pbl-invalid-contrast;
    }
}

.generator-visual-inspection__comment-text {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: pre-wrap;
    word-break: break-word;
}

.generator-visual-inspection__attachments-cell {
    display: flex;
    align-items: center;
    justify-content: center;
}

.generator-visual-inspection__attachments-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: $spacing-3xs;
    border: none;
    background: transparent;
    cursor: pointer;

    &:hover {
        opacity: 0.85;
    }

    &:focus-visible {
        outline: 2px solid $pbl-primary;
        outline-offset: 2px;
    }
}

.generator-visual-inspection__attachments-pbl-icon {
    --size: 1rem;
    display: block;

    &.text-primary {
        color: $pbl-primary-contrast;
    }
}

.generator-visual-inspection__tooltip {
    display: flex;
    flex-direction: column;
    gap: $spacing-s;

    background-color: $pbl-text !important;
    color: $pbl-foreground;

    max-width: 320px;

    &__content {
        white-space: pre-wrap;
    }
}
</style>
